/**
 * Purpose:
 *   Application header component with responsive design.
 *   Displays logo, portal name, notification icon, and user avatar.
 *
 * Notes:
 *   - Desktop: Shows full header with avatar dropdown
 *   - Mobile: Shows hamburger menu button to toggle sidebar
 *   - Uses gradient background defined in Tailwind config (bg-header)
 *   - i18n labels for avatar actions are provided by the protected layout
 */

'use client';

import React from 'react';
import Image from 'next/image';
import logo from '@/public/icons/1Global_white_icon.svg';
import { Avatar } from '../../ui/avatar';
import { Icon } from '../../ui/icon/Icon';
import { cx } from '@/util/functions/cx';
import { headerStyles } from './Header.styles';

interface HeaderProps {
  /** Extra classes for customization */
  className?: string;
  /** User display name for avatar */
  userName?: string;
  /** Whether the mobile menu is currently open */
  isMenuOpen?: boolean;
  /** Callback when hamburger menu button is clicked (mobile only) */
  onMenuToggle?: () => void;
  /** Callback when logout is triggered from avatar dropdown */
  onLogout?: () => void;
  /** Whether logout is in progress */
  isLoggingOut?: boolean;
  /** Callback when profile is triggered from avatar dropdown */
  onProfile?: () => void;
  /** Accessible label for the avatar menu */
  userMenuAriaLabel: string;
  /** Profile item label */
  profileLabel: string;
  /** Logout item label */
  logoutLabel: string;
  /** Logout loading label */
  loggingOutLabel: string;
}

export const Header: React.FC<HeaderProps> = ({
  className = '',
  userName = 'User',
  isMenuOpen = false,
  onMenuToggle,
  onLogout,
  isLoggingOut = false,
  onProfile,
  userMenuAriaLabel,
  profileLabel,
  logoutLabel,
  loggingOutLabel,
}) => {
  return (
    <header className={cx(headerStyles.base, 'bg-header', className)}>
      {/* Left section: Hamburger (mobile) + Logo + Portal Name */}
      <div className={headerStyles.logoContainer}>
        {/* Hamburger menu button - mobile only */}
        <button
          type="button"
          onClick={onMenuToggle}
          className={headerStyles.hamburgerButton}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMenuOpen}
        >
          <Icon
            src={isMenuOpen ? '/icons/x.svg' : '/icons/menu.svg'}
            size={24}
            className="text-white"
          />
        </button>

        <Image src={logo} alt="1GLOBAL" width={(41 / 54) * 24} height={24} />

        {/* Portal Name - desktop only */}
        <h1 className={headerStyles.portalName}>IoT Portal</h1>
      </div>

      {/* Right section: Notification + Avatar */}
      <div className={headerStyles.actionsContainer}>
        {/* User Avatar with dropdown */}
        <Avatar
          name={userName}
          onProfile={onProfile}
          onLogout={onLogout}
          isLoggingOut={isLoggingOut}
          menuAriaLabel={userMenuAriaLabel}
          profileLabel={profileLabel}
          logoutLabel={logoutLabel}
          loggingOutLabel={loggingOutLabel}
        />
      </div>
    </header>
  );
};
